import { RequestOptions } from '@crossnokaye/typescript-sdk/lib/sdks';

import { GaiaCore } from '@repo/sdk/core';
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  SDKError,
  SDKValidationError,
  UnexpectedClientError,
} from '@repo/sdk/models/errors';
import { Result, unwrapAsync } from '@repo/sdk/types/fp';

import { BACKEND_URL } from '@/constants';

type GaiaFuncWithRequest<TRequest, TResponse> = (
  core: GaiaCore,
  request: TRequest,
  options?: RequestOptions
) => Promise<
  Result<
    TResponse,
    | SDKError
    | SDKValidationError
    | UnexpectedClientError
    | InvalidRequestError
    | RequestAbortedError
    | RequestTimeoutError
    | ConnectionError
  >
>;

type GaiaFuncWithoutRequest<TResponse> = (
  core: GaiaCore,
  options?: RequestOptions
) => Promise<
  Result<
    TResponse,
    | SDKError
    | SDKValidationError
    | UnexpectedClientError
    | InvalidRequestError
    | RequestAbortedError
    | RequestTimeoutError
    | ConnectionError
  >
>;

interface GaiaPublicClient<TRequest, TResponse> {
  gaiaFunc: GaiaFuncWithRequest<TRequest, TResponse> | GaiaFuncWithoutRequest<TResponse>;
  request?: TRequest;
  options?: RequestOptions;
}

/**
 * A Gaia client for public endpoints that don't require authentication.
 * This is used for endpoints like application configuration that are accessed
 * before the user has authenticated.
 */
export const gaiaPublicClient = async <TRequest, TResponse>({
  gaiaFunc,
  request,
  options,
}: GaiaPublicClient<TRequest, TResponse>) => {
  // Instantiate new Gaia Core without authentication
  const gaiaCore = new GaiaCore({
    serverURL: BACKEND_URL.toString(),
  });

  if (request) {
    return unwrapAsync(
      (gaiaFunc as GaiaFuncWithRequest<TRequest, TResponse>)(gaiaCore, request, options)
    );
  }
  return unwrapAsync((gaiaFunc as GaiaFuncWithoutRequest<TResponse>)(gaiaCore, options));
};
